
import { useState } from "react";
import { Icon, Reveal, SERIF } from "./ui";
import { getInitials } from "./SectorsData";

/*
 * CouncilLeadership — PROPS-DRIVEN.
 * Pehle ye component hardcoded COUNCIL_MEMBERS (agri council) import karta
 * tha "./data" se — isliye HAR council page pe same leadership dikhti thi.
 * Ab ye sirf `members` prop render karta hai, jo SectorPage se aata hai:
 *
 *   <CouncilLeadership members={leadership} councilName={sector.title} />
 *
 * Card design SAME hai (Option A / Option B) — sirf content council ke
 * hisaab se badalta hai. Photo URL na ho ya toot jaye to initials tile
 * automatically dikh jati hai (photo: "https://..." add kar do, bas).
 */

/* ------------------------- SHARED PHOTO HANDLER ------------------------- */

function Portrait({ member, className = "", imgClass = "" }) {
  const [broken, setBroken] = useState(false);

  if (broken || !member.photo) {
    return (
      <span
        className={`flex items-center justify-center bg-gradient-to-br from-[#FEF7F4] to-[#FAF7F2] text-3xl font-bold text-[#DA2128] ${className}`}
      >
        {member.initials}
      </span>
    );
  }
  return (
    <img
      src={member.photo}
      alt={member.name}
      onError={() => setBroken(true)}
      loading="lazy"
      className={`${className} ${imgClass} object-cover object-top`}
    />
  );
}

/* ============================== OPTION A ==============================
   Profile Cards — horizontal card, framed portrait beside the details
====================================================================== */

function OptionACard({ member, delay = 0 }) {
  return (
    <Reveal delay={delay} variant="zoom">
      <article className="group relative flex h-full items-center gap-5 overflow-hidden rounded-3xl border border-stone-200 bg-white p-5 shadow-[0_2px_20px_-14px_rgba(28,25,23,.5)] transition-all duration-500 hover:-translate-y-1.5 hover:border-[#DA2128]/30 hover:shadow-2xl hover:shadow-[#DA2128]/12 sm:p-6">
        <span className="pointer-events-none absolute -right-14 -top-14 h-36 w-36 rounded-full bg-gradient-to-br from-[#FEF7F4] to-[#FAF7F2] transition-transform duration-700 group-hover:scale-150" />
        <Icon
          name="wheat"
          className="pointer-events-none absolute -bottom-4 right-2 h-24 w-24 text-[#DA2128]/5"
          strokeWidth={1}
        />

        <div className="relative shrink-0">
          <span className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-[#DA2128] via-[#DB6400] to-[#F59C00] opacity-80" />
          <Portrait
            member={member}
            className="relative h-[124px] w-[104px] rounded-2xl border-2 border-white sm:h-[150px] sm:w-[126px]"
            imgClass="transition-transform duration-700 group-hover:scale-[1.04]"
          />
        </div>

        <div className="relative min-w-0">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#DA2128] px-3 py-1 text-[10.5px] font-extrabold uppercase tracking-[0.14em] text-white">
            <Icon name="users" className="h-3 w-3" strokeWidth={2.4} />
            {member.role}
          </span>
          <h3
            style={SERIF}
            className="mt-3 text-[22px] font-bold leading-tight text-stone-900 transition-colors group-hover:text-[#DA2128] sm:text-2xl"
          >
            {member.name}
          </h3>
          <p className="mt-1.5 text-[13.5px] font-semibold leading-snug text-stone-600">
            {member.designation}
          </p>
          <span className="mt-4 flex items-center gap-2" aria-hidden="true">
            <span className="h-[3px] w-8 rounded-full bg-[#DA2128]" />
            <span className="h-[3px] w-3 rounded-full bg-[#F59C00]" />
          </span>
        </div>
      </article>
    </Reveal>
  );
}

/* ============================== OPTION B ==============================
   Portrait Spotlight — tall image, gradient overlay, details on the image
====================================================================== */

function OptionBCard({ member, index, councilName, delay = 0 }) {
  return (
    <Reveal delay={delay} variant="up">
      <article className="group relative h-full overflow-hidden rounded-[28px] bg-stone-950 shadow-xl shadow-stone-900/15 ring-1 ring-stone-900/5 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-[#DA2128]/25">
        {/* index watermark */}
        <span
          style={SERIF}
          className="pointer-events-none absolute right-5 top-3 z-20 select-none text-[64px] font-black leading-none text-white/15"
        >
          {String(index + 1).padStart(2, "0")}
        </span>

        <div className="relative h-[340px] w-full overflow-hidden sm:h-[380px]">
          <Portrait
            member={member}
            className="h-full w-full"
            imgClass="h-full w-full scale-[1.02] transition-transform duration-[1200ms] group-hover:scale-110"
          />
          <span className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/55 to-transparent" />
          <span className="absolute inset-0 bg-gradient-to-tr from-[#DA2128]/45 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

          {/* role ribbon */}
          <span className="absolute left-5 top-5 z-20 inline-flex items-center gap-1.5 rounded-full bg-[#DA2128] px-3.5 py-1.5 text-[10.5px] font-extrabold uppercase tracking-[0.16em] text-white shadow-lg shadow-[#DA2128]/40">
            <Icon name="users" className="h-3 w-3" strokeWidth={2.4} />
            {member.role}
          </span>

          {/* details over image */}
          <div className="absolute inset-x-0 bottom-0 z-20 p-6">
            <span className="flex items-center gap-2" aria-hidden="true">
              <span className="h-[3px] w-10 rounded-full bg-[#F59C00] transition-all duration-500 group-hover:w-16" />
              <span className="h-[3px] w-3 rounded-full bg-white/70" />
            </span>
            <h3
              style={SERIF}
              className="mt-3 text-[26px] font-bold leading-tight text-white sm:text-[28px]"
            >
              {member.name}
            </h3>
            <p className="mt-1.5 text-[13.5px] font-semibold leading-snug text-white/80">
              {member.designation}
            </p>
            <span className="mt-4 inline-flex items-center gap-2 text-[11.5px] font-extrabold uppercase tracking-[0.16em] text-[#F59C00] opacity-0 transition-all duration-500 group-hover:opacity-100">
              {councilName}
              <Icon name="arrow" className="h-3.5 w-3.5" strokeWidth={2.6} />
            </span>
          </div>
        </div>
      </article>
    </Reveal>
  );
}

/* ============================ SECTION SHELL ============================ */

const OPTIONS = [
  { key: "A", label: "Option A", hint: "Profile Cards" },
  // { key: "B", label: "Option B", hint: "Portrait Spotlight" },
];

export default function CouncilLeadership({
  members = [],
  councilName = "",
  councilTitle = "", // fallback alias (agar kahin councilTitle pass ho raha ho)
  defaultOption = "A",
}) {
  const [option, setOption] = useState(defaultOption);
  const name = councilName || councilTitle;

  const list = members.map((m) => ({
    ...m,
    initials: m.initials || getInitials(m.name),
  }));

  /* ---------- Empty state (e.g. Toy Council — TBC) ---------- */
  if (!list.length) {
    return (
      <div className="mx-auto mt-10 flex max-w-2xl flex-col items-center rounded-[24px] border border-dashed border-[#DA2128]/25 bg-white/70 px-8 py-14 text-center">
        <div className="grid h-16 w-16 place-items-center rounded-3xl bg-[#FEF7F4]">
          <Icon name="users" className="h-7 w-7 text-[#DA2128]" strokeWidth={1.8} />
        </div>
        <h4 style={SERIF} className="mt-5 text-lg font-bold text-stone-900">
          Leadership to be announced
        </h4>
        <p className="mt-2 max-w-sm text-[13px] text-stone-500">
          The Council’s leadership is being finalized by the secretariat. Until then, you can contact the secretariat using the form given below.
        </p>
      </div>
    );
  }

  return (
    <>
   

      {/* ---------------------------- OPTION A ---------------------------- */}
      {option === "A" && (
        <div key="opt-a" className="mx-auto mt-10 grid max-w-5xl gap-6 md:grid-cols-2">
          {list.map((m, i) => (
            <OptionACard key={m.name} member={m} delay={i * 120} />
          ))}
        </div>
      )}

      {/* ---------------------------- OPTION B ---------------------------- */}
      {option === "B" && (
        <div key="opt-b" className="mx-auto mt-10 grid max-w-5xl gap-6 sm:grid-cols-2">
          {list.map((m, i) => (
            <OptionBCard key={m.name} member={m} index={i} councilName={name} delay={i * 120} />
          ))}
        </div>
      )}
    </>
  );
}
